<template>
  <div class="ticket-arquivos pa-4 pr-3 mr-1" style="max-height: 80vh; overflow-y: auto;">
    <!-- Carregando -->
    <div v-if="carregando" class="d-flex flex-column align-center justify-center py-8">
      <v-progress-circular indeterminate color="primary" size="40" />
      <span class="mt-3 grey--text caption">Buscando arquivos...</span>
    </div>

    <!-- Sem arquivos -->
    <div v-else-if="!arquivos.length" class="text-center grey--text py-8">
      <v-icon color="grey lighten-1" size="48">mdi-folder-open-outline</v-icon>
      <p class="mt-3 body-2">Nenhum arquivo foi enviado neste ticket.</p>
    </div>

    <!-- Lista de arquivos -->
    <template v-else>
      <v-list dense rounded class="transparent elevation-2 rounded-lg pa-0" >
        <div v-for="(arquivo, index) in arquivos" :key="arquivo.path || index">
          <v-list-item class="py-2">
            <v-list-item-avatar size="36" :color="corAvatar(arquivo)" class="mr-3">
              <v-icon :color="corIcone(arquivo)" size="20">{{ icone(arquivo) }}</v-icon>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title class="file-title font-weight-medium text-body-2 text-wrap " style="word-break: break-all;">
                {{ nomeExibicao(arquivo) }}
              </v-list-item-title>
              <v-list-item-subtitle class="caption grey--text" v-if="arquivo.size">
                {{ formatarTamanho(arquivo.size) }}
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action>
              <v-btn
                icon
                color="primary"
                :loading="baixando === (arquivo.path || index)"
                :disabled="!!baixando"
                @click="baixar(arquivo)"
                title="Download"
              >
                <v-icon>mdi-download</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
          <v-divider v-if="index < arquivos.length - 1" inset />
        </div>
      </v-list>
    </template>

    <!-- Snackbar de erro -->
    <v-snackbar v-model="erro" color="error" :timeout="4000" bottom>
      Erro ao baixar o arquivo. Tente novamente.
      <template v-slot:action>
        <v-btn text icon @click="erro = false"><v-icon>mdi-close</v-icon></v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import API from '@/api/API'

export default {
  name: 'TicketArquivos',
  props: {
    ticketId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    arquivos: [],
    carregando: true,
    baixando: null,
    erro: false,
  }),
  async mounted() {
    await this.buscarArquivos()
  },
  methods: {
    async buscarArquivos() {
      this.carregando = true
      try {
        this.arquivos = await API.buscarArquivosOcorrencia(this.ticketId)
      } finally {
        this.carregando = false
      }
    },
    async baixar(arquivo) {
      const chave = arquivo.path || arquivo.name
      this.baixando = chave
      try {
        // Extrai o fileName no mesmo formato que o PROGALI-CLIENTES usa:
        // arquivo.path = "{id}/{tipo}/{nome}" — remove o prefixo do id
        const id = this.ticketId.split('_')[1] || this.ticketId
        const fileName = arquivo.path
          ? arquivo.path.replace(`${id}/`, '')
          : arquivo.name

        const signedUrl = await API.getArquivoSignedUrl(this.ticketId, fileName)
        this.simularDownload(this.nomeExibicao(arquivo), signedUrl)
      } catch (e) {
        this.erro = true
      } finally {
        this.baixando = null
      }
    },
    simularDownload(name, url) {
      // Mesmo padrão de simularClickDownload do ArquivosDoTikcet.vue
      const a = document.createElement('A')
      a.href = url
      a.download = name
      a.target = '_blank'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    nomeExibicao(arquivo) {
      // Extrai o nome real do path: "{id}/OCORRENCIA/nome-do-arquivo.ext"
      if (arquivo.path) {
        const partes = arquivo.path.split('/')
        return partes[partes.length - 1]
      }
      return arquivo.name || 'Arquivo'
    },
    icone(arquivo) {
      const nome = this.nomeExibicao(arquivo).toLowerCase()
      if (/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/.test(nome)) return 'mdi-file-image'
      if (/\.pdf$/.test(nome)) return 'mdi-file-pdf-box'
      if (/\.(doc|docx)$/.test(nome)) return 'mdi-file-word'
      if (/\.(xls|xlsx)$/.test(nome)) return 'mdi-file-excel'
      if (/\.zip$/.test(nome)) return 'mdi-folder-zip'
      return 'mdi-file'
    },
    corIcone(arquivo) {
      const nome = this.nomeExibicao(arquivo).toLowerCase()
      if (/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/.test(nome)) return '#1565C0'
      if (/\.pdf$/.test(nome)) return '#C62828'
      if (/\.(doc|docx)$/.test(nome)) return '#1565C0'
      if (/\.(xls|xlsx)$/.test(nome)) return '#2E7D32'
      return '#350061'
    },
    corAvatar(arquivo) {
      const nome = this.nomeExibicao(arquivo).toLowerCase()
      if (/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/.test(nome)) return '#E3F2FD'
      if (/\.pdf$/.test(nome)) return '#FFEBEE'
      if (/\.(xls|xlsx)$/.test(nome)) return '#E8F5E9'
      return '#EDE7F6'
    },
    formatarTamanho(bytes) {
      if (!bytes) return ''
      return bytes > 1048576
        ? `${(bytes / 1048576).toFixed(1)} MB`
        : `${(bytes / 1024).toFixed(0)} KB`
    },
  },
}
</script>

<style scoped>
/* Cor padrão (escuro) */
.file-title {
  color: #333; 
}
/* Cor branca apenas em telas grandes (Desktop) */
/* @media (min-width: 960px) {
  .file-title {
    color: white !important;
  }
} */
</style>
